import { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { Code128Barcode } from '../../components/ui/Code128Barcode';
import { ErrorState, LoadingState } from '../../components/ui/Feedback';
import { doctorNameOrDash } from '../../lib/doctor-name';
import { formatDateTime } from '../../lib/datetime';
import { useHospitalTitle } from '../../lib/hospital-brand';
import { useDocumentHeaderLogo } from '../settings/settings.queries';
import { useInvestigationBill } from './investigation-bill.queries';

const PRESCRIPTION_HEADER_NAME = 'opd_prescription';

export const InvestigationBillPrescriptionPrintPage = () => {
  const { id } = useParams();
  const [params] = useSearchParams();
  const withHeader = params.get('header') === '1';
  const hospitalTitle = useHospitalTitle();
  const query = useInvestigationBill(id);
  const headerLogoQuery = useDocumentHeaderLogo(PRESCRIPTION_HEADER_NAME, withHeader);

  useEffect(() => {
    if (query.data && (!withHeader || headerLogoQuery.isFetched)) window.setTimeout(() => window.print(), 300);
  }, [query.data, withHeader, headerLogoQuery.isFetched]);

  if (query.isLoading) return <LoadingState />;
  if (query.isError || !query.data) return <ErrorState title="Investigation bill not found" message="The bill could not be loaded." />;

  const row = query.data;
  const patient = row.patientSnapshot;
  const logo = headerLogoQuery.data;
  const age = [
    patient.ageYears ? `${patient.ageYears}Y` : '',
    patient.ageMonths ? `${patient.ageMonths}M` : '',
    patient.ageDays ? `${patient.ageDays}D` : '',
  ].filter(Boolean).join(' ');
  const address = [patient.address, patient.district, patient.state, patient.pinCode].filter(Boolean).join(',');
  const vitals = ['Height : ', 'Weight : ', 'BP : ', 'Temp : ', 'Pulse : ', 'RR : '];

  return (
    <div className="print-prescription-page">
      <div className="print-prescription-toolbar no-print">
        <button type="button" onClick={() => window.print()}>Print</button>
        <button type="button" className="danger" onClick={() => window.close()}>Close</button>
      </div>

      {withHeader && logo ? (
        <table className="print-prescription-logo-table"><tbody><tr><td><img src={logo} alt={hospitalTitle} /></td></tr></tbody></table>
      ) : (
        <div className="print-prescription-header-space" aria-hidden="true" />
      )}

      <table className="print-prescription-info-row"><tbody><tr>
        <td className="print-prescription-info-cell">Date : {formatDateTime(row.billDate)}</td>
        <td className="print-prescription-info-cell">{patient.uhid ? <Code128Barcode value={patient.uhid} /> : null}</td>
        <td className="print-prescription-info-cell">Investigation No : {row.investigationNumber}</td>
        <td className="print-prescription-info-cell">Bill No : {row.billing.billNumber}</td>
      </tr></tbody></table>

      <table className="print-prescription-details"><tbody><tr>
        <td className="print-prescription-patient-cell">
          <p>PATIENT NAME : <b>{patient.name}</b> {patient.uhid ? `(UHID : ${patient.uhid})` : ''}</p>
          <p>GENDER : <b>{patient.gender}</b> || AGE : <b>{age}</b></p>
          <p>MOB NO : <b>{patient.phone ?? ''}</b></p>
          <p>Address : {address}</p>
        </td>
        <td className="print-prescription-doctor-cell">
          <p>REFERRING DOCTOR : </p>
          <p>{doctorNameOrDash(row.referringDoctor?.name)}</p>
          <p>{row.department?.name ?? ''}</p>
          <p>{row.referredBy ? `REFERRED BY - ${row.referredBy.name}` : ''}</p>
          <p>{row.provider ? `PROVIDER - ${row.provider.name}` : ''}</p>
        </td>
      </tr></tbody></table>

      <table className="print-prescription-vitals"><tbody><tr>
        {vitals.map((label) => <td key={label}><b>{label}</b></td>)}
      </tr></tbody></table>
    </div>
  );
};
